/**
 * Departments API — TanStack Query factory.
 *
 * Phase-2 Wave-3 W3.T2. Departments rarely change inside a session, so we
 * cache aggressively (`staleTime: 5 minutes`) — the Employees page reads
 * this for the dept-filter dropdown on every tab switch and we don't
 * want to round-trip on each one.
 *
 * BE shape (DepartmentRow):
 *   `{ id: number, name: string, slug: string, hasActiveManager: boolean }`.
 *
 * The endpoint excludes archived rows by default (Phase 1 service line
 * 26-30); a future `?include_archived` is reserved but not consumed
 * here.
 *
 * R6.2: `hasActiveManager` is true when the dept currently has an
 * ACTIVE MANAGER-tier (РО) user. The invite-create modal uses this
 * flag to filter the dropdown when the actor picks a MANAGER role.
 */
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/fetcher';
import { qk } from '@/lib/query-keys';

export interface DepartmentRow {
  id: number;
  name: string;
  slug: string;
  /** R6.2: true when the dept has an ACTIVE MANAGER-tier user. */
  hasActiveManager: boolean;
}

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export function useDepartmentsQuery() {
  return useQuery<DepartmentRow[]>({
    queryKey: qk.departments.list(),
    queryFn: () => apiFetch<DepartmentRow[]>('/departments'),
    staleTime: FIVE_MINUTES_MS,
  });
}
